// Copyright (c) Ping. All rights reserved.
//
// Call signaling — the cross-platform core of the call feature.
//
// Calls reuse the in-chat AppEvent transport that games already prove out:
// the initiator `conv.send`s a `com.ping.call_invite` envelope on the HOST
// conversation, and every member device receives it through the same message
// stream that drives chat. The handful of call lifecycle events
// (invite / ringing / accept / decline / end) plus the in-call affordances
// (hand-raise / reaction) fold into a `CallSignalSession` projection keyed by
// `call_id`. This module is pure and platform-agnostic so iOS / desktop reuse
// it verbatim (see docs/ios-parity, docs/desktop-parity).
//
// Media (LiveKit) and the per-call MLS group live in the platform host
// (apps/web/src/calls). This file never imports either.

import { encodeEnvelopeJsonBytes } from '../appEvent/envelope';
import type { ParseResult } from '../appEvent/envelope';
import type {
  CallAcceptBody,
  CallDeclineBody,
  CallEndBody,
  CallHandRaiseBody,
  CallHandoffBody,
  CallInviteBody,
  CallReactionBody,
  CallRosterBody,
} from '../appEvent/types';

export type CallMedia = 'audio' | 'video';

// ---------------------------------------------------------------------------
// id helpers — call_id is a 16-byte ULID-ish id, lowercase hex on the wire and
// as the LiveKit room name (matches the backend's decodeCallID).
// ---------------------------------------------------------------------------

export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i]!.toString(16).padStart(2, '0');
  }
  return out;
}

export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.length % 2 === 0 ? hex : `0${hex}`;
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/** 16 random bytes, hex-encoded — the canonical call_id / room name. */
export function newCallIdHex(): string {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return bytesToHex(buf);
}

// ---------------------------------------------------------------------------
// Envelope builders. Each returns an `EncodableEnvelope` the host serializes
// with `encodeEnvelopeJsonBytes` and hands to `conv.send`. Shapes match the
// decoders in appEvent/envelope.ts exactly.
// ---------------------------------------------------------------------------

export function buildInvite(body: CallInviteBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_invite', payload: { ...body } });
}

export function buildRinging(callId: string): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_ringing', payload: { call_id: callId } });
}

export function buildAccept(body: CallAcceptBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_accept', payload: { ...body } });
}

export function buildDecline(body: CallDeclineBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_decline', payload: { ...body } });
}

export function buildEnd(body: CallEndBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_end', payload: { ...body } });
}

export function buildHandRaise(body: CallHandRaiseBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_hand_raise', payload: { ...body } });
}

export function buildReaction(body: CallReactionBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_reaction', payload: { ...body } });
}

/** Live-roster presence ping — see `CallRosterBody` / CallType.CALL_ROSTER. */
export function buildRoster(body: CallRosterBody): Uint8Array {
  return encodeEnvelopeJsonBytes({
    kind: 'com.ping.call_roster',
    payload: { call_id: body.call_id, participants: [...body.participants] },
  });
}

export function buildHandoff(body: CallHandoffBody): Uint8Array {
  return encodeEnvelopeJsonBytes({ kind: 'com.ping.call_handoff', payload: { ...body } });
}

// ---------------------------------------------------------------------------
// Decoded, host-fed events. The host attaches the transport metadata the
// envelope body doesn't carry: who sent it (account id), the CAS-ordered
// `serverSeq` (used for first-accept-wins), and the receive timestamp.
// ---------------------------------------------------------------------------

export interface CallSignalContext {
  /** Sender's account id (base64url). */
  readonly from: string;
  /** Sender's device id (base64url) when known. */
  readonly fromDevice?: string | undefined;
  /** Conversation the envelope arrived on (host conversation id). */
  readonly conversationId: string;
  /** CAS-ordered server sequence — lower wins ties. */
  readonly serverSeq: number;
  /** Receive/sent time, ms epoch. */
  readonly at: number;
  /** Whether this device authored the event (own echo). */
  readonly isSelf: boolean;
  /** Content hash (hex) of THIS signaling message — for call_invite it's the
   *  thread root every participant threads in-call replies under (same on all
   *  sides, content-addressed). */
  readonly contentHash?: string | undefined;
}

export type CallSignalEvent =
  | ({ readonly type: 'invite' } & CallInviteBody & CallSignalContext)
  | ({ readonly type: 'ringing'; readonly call_id: string } & CallSignalContext)
  | ({ readonly type: 'accept' } & CallAcceptBody & CallSignalContext)
  | ({ readonly type: 'decline' } & CallDeclineBody & CallSignalContext)
  | ({ readonly type: 'end' } & CallEndBody & CallSignalContext)
  | ({ readonly type: 'hand_raise' } & CallHandRaiseBody & CallSignalContext)
  | ({ readonly type: 'reaction' } & CallReactionBody & CallSignalContext)
  | ({ readonly type: 'roster' } & CallRosterBody & CallSignalContext)
  | ({ readonly type: 'handoff' } & CallHandoffBody & CallSignalContext);

/**
 * Maps a parsed AppEvent + transport context to a `CallSignalEvent`, or null
 * if the envelope is not call signaling. The host calls this for every
 * incoming (and own-echo) message and feeds the result to `reduceCallSignal`.
 */
export function parsedToCallSignal(
  parsed: ParseResult | undefined,
  ctx: CallSignalContext,
): CallSignalEvent | null {
  if (!parsed || parsed.kind === 'unrenderable' || !('body' in parsed)) return null;
  switch (parsed.kind) {
    case 'com.ping.call_invite':
      return { type: 'invite', ...(parsed.body as CallInviteBody), ...ctx };
    case 'com.ping.call_ringing':
      return { type: 'ringing', call_id: (parsed.body as { call_id: string }).call_id, ...ctx };
    case 'com.ping.call_accept':
      return { type: 'accept', ...(parsed.body as CallAcceptBody), ...ctx };
    case 'com.ping.call_decline':
      return { type: 'decline', ...(parsed.body as CallDeclineBody), ...ctx };
    case 'com.ping.call_end':
      return { type: 'end', ...(parsed.body as CallEndBody), ...ctx };
    case 'com.ping.call_hand_raise':
      return { type: 'hand_raise', ...(parsed.body as CallHandRaiseBody), ...ctx };
    case 'com.ping.call_reaction':
      return { type: 'reaction', ...(parsed.body as CallReactionBody), ...ctx };
    case 'com.ping.call_roster':
      return { type: 'roster', ...(parsed.body as CallRosterBody), ...ctx };
    case 'com.ping.call_handoff':
      return { type: 'handoff', ...(parsed.body as CallHandoffBody), ...ctx };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Folded session projection.
// ---------------------------------------------------------------------------

export type CallSignalStatus = 'ringing' | 'accepted' | 'declined' | 'ended';

export interface CallReactionFlash {
  readonly fromAccount: string;
  readonly emoji: string;
  readonly at: number;
}

export interface CallSignalSession {
  readonly callId: string;
  readonly callGroupId: string;
  readonly conversationId: string;
  readonly media: CallMedia;
  readonly initiatorDevice: string;
  readonly initiatorAccount: string;
  readonly actorDisplayName?: string;
  /** True for group (huddle) calls — drives the callee's grid + hangup rule. */
  readonly group?: boolean;
  readonly status: CallSignalStatus;
  /** Device that won the accept race (first by serverSeq). */
  readonly acceptedByDevice?: string;
  readonly acceptedByAccount?: string;
  /** serverSeq of the winning accept — guards the first-accept-wins race. */
  readonly firstAcceptSeq?: number;
  readonly endedReason?: string;
  /** call_invite content hash (hex) — the in-call thread root. */
  readonly contentHash?: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Accounts with a raised hand. */
  readonly raisedHands: ReadonlySet<string>;
  /** Most recent reaction flash, if any. */
  readonly lastReaction?: CallReactionFlash;
}

/**
 * Folds one `CallSignalEvent` into the session map, returning a NEW map.
 * Non-invite events for an unknown call synthesize a minimal session (the
 * invite may not have replayed yet). Terminal states (declined/ended) are
 * sticky. Accept resolves first-by-serverSeq (calls.md §6).
 */
export function reduceCallSignal(
  prev: ReadonlyMap<string, CallSignalSession>,
  evt: CallSignalEvent,
): Map<string, CallSignalSession> {
  const next = new Map(prev);
  const existing = next.get(evt.call_id);

  switch (evt.type) {
    case 'invite': {
      if (existing) return prev as Map<string, CallSignalSession>;
      next.set(evt.call_id, {
        callId: evt.call_id,
        callGroupId: evt.call_group_id,
        conversationId: evt.conversationId,
        media: evt.media,
        initiatorDevice: evt.initiator_device,
        initiatorAccount: evt.from,
        ...(evt.actorDisplayName !== undefined ? { actorDisplayName: evt.actorDisplayName } : {}),
        ...(evt.contentHash !== undefined ? { contentHash: evt.contentHash } : {}),
        ...(evt.group !== undefined ? { group: evt.group } : {}),
        status: 'ringing',
        createdAt: evt.at,
        updatedAt: evt.at,
        raisedHands: new Set<string>(),
      });
      return next;
    }
    case 'ringing': {
      // Pure liveness ack from a callee device; no state change beyond a
      // touch so the caller UI can flip "Calling…" → "Ringing…".
      if (!existing) return prev as Map<string, CallSignalSession>;
      next.set(evt.call_id, { ...existing, updatedAt: evt.at });
      return next;
    }
    case 'accept': {
      const base = existing ?? synthesize(evt.call_id, evt.call_group_id, evt.conversationId, evt.at);
      if (base.status === 'ended' || base.status === 'declined') {
        return prev as Map<string, CallSignalSession>;
      }
      // First-accept-wins: keep the lowest serverSeq.
      if (base.firstAcceptSeq !== undefined && base.firstAcceptSeq <= evt.serverSeq) {
        return prev as Map<string, CallSignalSession>;
      }
      next.set(evt.call_id, {
        ...base,
        status: 'accepted',
        acceptedByDevice: evt.accepting_device,
        acceptedByAccount: evt.from,
        firstAcceptSeq: evt.serverSeq,
        updatedAt: evt.at,
      });
      return next;
    }
    case 'decline': {
      const base = existing ?? synthesize(evt.call_id, '', evt.conversationId, evt.at);
      // A decline doesn't override an already-accepted call (another device
      // answered); it only ends a still-ringing one.
      if (base.status === 'accepted' || base.status === 'ended') {
        return prev as Map<string, CallSignalSession>;
      }
      next.set(evt.call_id, { ...base, status: 'declined', endedReason: evt.reason, updatedAt: evt.at });
      return next;
    }
    case 'end': {
      const base = existing ?? synthesize(evt.call_id, '', evt.conversationId, evt.at);
      next.set(evt.call_id, { ...base, status: 'ended', endedReason: evt.reason, updatedAt: evt.at });
      return next;
    }
    case 'hand_raise': {
      if (!existing) return prev as Map<string, CallSignalSession>;
      const raised = new Set(existing.raisedHands);
      if (evt.raised) raised.add(evt.from);
      else raised.delete(evt.from);
      next.set(evt.call_id, { ...existing, raisedHands: raised, updatedAt: evt.at });
      return next;
    }
    case 'reaction': {
      if (!existing) return prev as Map<string, CallSignalSession>;
      next.set(evt.call_id, {
        ...existing,
        lastReaction: { fromAccount: evt.from, emoji: evt.emoji, at: evt.at },
        updatedAt: evt.at,
      });
      return next;
    }
    case 'handoff':
      // Handoff is handled imperatively by the host (prompt + join); it does
      // not mutate the ring/session projection.
      return prev as Map<string, CallSignalSession>;
    case 'roster':
      // Live-roster presence pings drive the ongoing-tile roster in the call
      // store (`liveRosterByCallId`), not the ring/session projection.
      return prev as Map<string, CallSignalSession>;
    default: {
      const _exhaustive: never = evt;
      void _exhaustive;
      return prev as Map<string, CallSignalSession>;
    }
  }
}

function synthesize(
  callId: string,
  callGroupId: string,
  conversationId: string,
  at: number,
): CallSignalSession {
  return {
    callId,
    callGroupId,
    conversationId,
    media: 'audio',
    initiatorDevice: '',
    initiatorAccount: '',
    status: 'ringing',
    createdAt: at,
    updatedAt: at,
    raisedHands: new Set<string>(),
  };
}
